/**
 * compare-slider.js — drag/touch controller for the shared <x-compare-slider>.
 *
 * Component contract: each compare-slider renders a wrapper with [data-cs-handle]
 * inside it. This script attaches drag/touch listeners that emit position updates
 * via the consumer-supplied onPosChange callback. The tool decides what to do
 * with the position (redraw a canvas, update clip-path on a video, etc.) — the
 * slider itself is purely chrome + drag math.
 *
 * Modes:
 *   'split'  — handle + both labels visible, drag enabled (default)
 *   'before' — handle + after-label hidden; show full Before
 *   'after'  — handle + before-label hidden; show full After
 *   'side'   — handle hidden, both labels visible (consumer renders side-by-side)
 *
 * Public API:
 *   window.CompareSlider.attach(elOrSelector, opts) → { getPos, setPos, setMode, destroy }
 *   window.CompareSlider.detach(elOrSelector)
 */
(function (root) {
    'use strict';

    var instances = new WeakMap();

    function resolveEl(target) {
        if (typeof target === 'string') return document.querySelector(target);
        if (target && target.nodeType === 1) return target;
        return null;
    }

    function attach(target, opts) {
        var el = resolveEl(target);
        if (!el) return null;
        // Already attached? Return existing.
        var existing = instances.get(el);
        if (existing) return existing;

        opts = opts || {};
        var minPos = typeof opts.minPos === 'number' ? opts.minPos : 0.02;
        var maxPos = typeof opts.maxPos === 'number' ? opts.maxPos : 0.98;
        // 'anywhere' (default): touch anywhere starts a drag — right for tool
        // pages where the comparator IS the workspace. 'handle': a TOUCH only
        // grabs the slider near the handle, so a finger on the photo still
        // scrolls the page — required inside feeds (/whats-new), where a
        // full-width preventDefault'ed frame becomes a scroll trap. Mouse
        // behaviour is identical in both modes.
        var dragFrom = opts.dragFrom === 'handle' ? 'handle' : 'anywhere';
        var initial = typeof opts.initialPos === 'number'
            ? opts.initialPos
            : parseFloat(el.dataset.initialPos) || 0.5;
        var onPosChange = typeof opts.onPosChange === 'function' ? opts.onPosChange : function () {};

        var handle = el.querySelector('[data-cs-handle]');
        var labelBefore = el.querySelector('[data-cs-label="before"]');
        var labelAfter = el.querySelector('[data-cs-label="after"]');
        var pos = clamp(initial);
        var mode = 'split';
        var dragging = false;

        function clamp(p) {
            if (!isFinite(p)) return 0.5;
            return Math.max(minPos, Math.min(maxPos, p));
        }
        function applyPos() {
            if (handle) { handle.style.left = (pos * 100) + '%'; handle.setAttribute('aria-valuenow', Math.round(pos * 100)); }
            try { onPosChange(pos); } catch (_) {}
        }
        function applyMode(m) {
            mode = m;
            el.dataset.mode = m;
            if (m === 'split') {
                handle && handle.classList.remove('cs-hidden');
                labelBefore && labelBefore.classList.remove('cs-hidden');
                labelAfter && labelAfter.classList.remove('cs-hidden');
            } else if (m === 'side') {
                handle && handle.classList.add('cs-hidden');
                labelBefore && labelBefore.classList.remove('cs-hidden');
                labelAfter && labelAfter.classList.remove('cs-hidden');
            } else if (m === 'before') {
                handle && handle.classList.add('cs-hidden');
                labelBefore && labelBefore.classList.remove('cs-hidden');
                labelAfter && labelAfter.classList.add('cs-hidden');
            } else if (m === 'after') {
                handle && handle.classList.add('cs-hidden');
                labelBefore && labelBefore.classList.add('cs-hidden');
                labelAfter && labelAfter.classList.remove('cs-hidden');
            }
            // Only tab-reachable when the handle is actually usable (split mode).
            if (handle) handle.setAttribute('tabindex', m === 'split' ? '0' : '-1');
        }

        function pointerX(e) {
            if (e.touches && e.touches.length) return e.touches[0].clientX;
            return e.clientX;
        }
        function startDrag(e) {
            if (mode !== 'split') return;
            if (e.touches && dragFrom === 'handle') {
                // finger must land near the handle line; elsewhere the touch
                // stays untouched and the page scrolls normally
                var rect0 = el.getBoundingClientRect();
                var hx = rect0.left + pos * rect0.width;
                if (Math.abs(pointerX(e) - hx) > 28) return;
            }
            dragging = true;
            // Update on pointerdown so a click anywhere snaps the handle there
            updateFromEvent(e);
            e.preventDefault();
        }
        function moveDrag(e) {
            if (!dragging || mode !== 'split') return;
            updateFromEvent(e);
            // Prevent page scroll while dragging on touch
            if (e.touches) e.preventDefault();
        }
        function endDrag() { dragging = false; }
        function updateFromEvent(e) {
            var rect = el.getBoundingClientRect();
            var x = pointerX(e) - rect.left;
            pos = clamp(x / rect.width);
            applyPos();
        }

        el.addEventListener('mousedown', startDrag);
        el.addEventListener('touchstart', startDrag, { passive: false });
        document.addEventListener('mousemove', moveDrag);
        document.addEventListener('touchmove', moveDrag, { passive: false });
        document.addEventListener('mouseup', endDrag);
        document.addEventListener('touchend', endDrag);
        document.addEventListener('touchcancel', endDrag);

        // a11y: keyboard-operable slider handle (WCAG 2.1.1). Benefits every
        // consumer tool, not just video-colorize. Arrow keys nudge, Shift = 5×,
        // Home/End jump to the ends.
        function onKey(e) {
            if (mode !== 'split') return;
            var step = e.shiftKey ? 0.1 : 0.02, p = pos;
            switch (e.key) {
                case 'ArrowLeft': case 'ArrowDown':  p = pos - step; break;
                case 'ArrowRight': case 'ArrowUp':   p = pos + step; break;
                case 'Home': p = minPos; break;
                case 'End':  p = maxPos; break;
                default: return;
            }
            e.preventDefault(); pos = clamp(p); applyPos();
        }
        if (handle) {
            handle.setAttribute('role', 'slider');
            if (!handle.hasAttribute('aria-label')) handle.setAttribute('aria-label', ((window.__commonStrings||{}).js_before_after || "Before \/ after comparison"));
            handle.setAttribute('aria-valuemin', '0');
            handle.setAttribute('aria-valuemax', '100');
            handle.setAttribute('aria-orientation', 'horizontal');
            handle.addEventListener('keydown', onKey);
        }

        // Apply initial state
        applyMode('split');
        applyPos();

        var api = {
            getPos: function () { return pos; },
            setPos: function (p) { pos = clamp(p); applyPos(); },
            setMode: function (m) { applyMode(m); },
            getMode: function () { return mode; },
            destroy: function () {
                el.removeEventListener('mousedown', startDrag);
                el.removeEventListener('touchstart', startDrag);
                document.removeEventListener('mousemove', moveDrag);
                document.removeEventListener('touchmove', moveDrag);
                document.removeEventListener('mouseup', endDrag);
                document.removeEventListener('touchend', endDrag);
                document.removeEventListener('touchcancel', endDrag);
                if (handle) handle.removeEventListener('keydown', onKey);
                instances.delete(el);
            },
        };
        instances.set(el, api);
        return api;
    }

    function detach(target) {
        var el = resolveEl(target);
        if (!el) return;
        var inst = instances.get(el);
        if (inst) inst.destroy();
    }

    root.CompareSlider = { attach: attach, detach: detach };
})(typeof window !== 'undefined' ? window : globalThis);
